import React from 'react';
import { createPortal } from 'react-dom';
import * as Popover from '@radix-ui/react-popover';
import { Button } from '@/ui/components/Button';
import styles from './CookiePopOver.module.scss';
import { Link } from 'react-router-dom';
import { Icon } from '@/ui/icons/Icon';
import { cookiesEu } from '@/assets/shared';
import { externalUrls } from '@/constants/urls';
import { useCookie } from '@/providers/Cookie';

export interface CookiePopOverProps {
  /**
   * Child element to attach the popover to (anchor)
   */
  children: React.ReactElement;
  /**
   * URL for cookie policy link
   */
  cookiePolicyUrl?: string;

  /**
   * Variant for responsive behavior
   */
  variant?: 'mobile' | 'desktop';
}

export function CookiePopOver({
  children,
  variant = 'mobile',
}: CookiePopOverProps) {
  const { acceptAll, isPopoverOpen, setIsCookiePreferencesOpen, hidePopover } =
    useCookie();

  const handleOpenChange = () => {};

  const handleClose = () => {
    hidePopover();
  };

  const handleAccept = () => {
    acceptAll();
  };

  const handleSettings = () => {
    setIsCookiePreferencesOpen(true);
  };

  // Calculate responsive offsets similar to ErrorHeaderPopover
  const calculatedSideOffset = variant === 'mobile' ? 25 : 18;
  const calculatedAlignOffset = variant === 'mobile' ? 0 : -18;

  return (
    <>
      {/* Overlay - rendered separately using createPortal */}
      {isPopoverOpen &&
        createPortal(
          <div
            className={styles.overlay}
            onClick={handleClose}
            aria-hidden="true"
          />,
          document.body
        )}

      <Popover.Root open={isPopoverOpen} onOpenChange={handleOpenChange}>
        <Popover.Anchor asChild>{children}</Popover.Anchor>

        <Popover.Portal>
          <Popover.Content
            className={styles.popoverContent}
            side="bottom"
            align="end"
            sideOffset={calculatedSideOffset}
            alignOffset={calculatedAlignOffset}
          >
            <div className={styles.popover}>
              <Icon name="vector-up" className={styles.popoverArrowIcon} />
              {/* Close Button */}
              <Button
                trackingId="close_cookie_popover"
                variant="text"
                size="xsmall"
                className={styles.closeButton}
                onClick={handleClose}
                aria-label="Close cookie banner"
                iconOnly={true}
                rightIcon={<Icon name="times" className={styles.closeIcon} />}
              />

              {/* Cookies EU Icon */}
              <div className={styles.cookiesIcon}>
                <img
                  src={cookiesEu}
                  alt="Cookies EU"
                  className={styles.cookiesIconImage}
                />
              </div>

              {/* Title */}
              <div className={styles.titleContainer}>
                <h2 className={styles.title}>
                  <span>Cookie-urile ne </span>
                  <span>îndulcesc viața</span>
                </h2>
              </div>

              {/* Description */}
              <p className={styles.description}>
                Acceptând stocarea cookie-urilor, experiența ta cu Ocean Credit
                e mult mai sigură și optimizată pentru preferințele tale. Află
                mai multe din{' '}
                <Link
                  to={externalUrls.volt.cookiePolicy}
                  target="_blank"
                  className={styles.link}
                >
                  Politica Cookies
                </Link>
              </p>

              {/* Buttons */}
              <div className={styles.buttons}>
                <Button
                  trackingId="accept_all_cookies"
                  variant="filled"
                  size="medium"
                  text="Accept cookie-urile"
                  onClick={handleAccept}
                  className={styles.acceptButton}
                  data-testid={'btn-cookie-pop-over-accept'}
                />
                <Button
                  trackingId="open_cookie_settings"
                  variant="outlined"
                  size="medium"
                  text="Setări cookie-uri"
                  onClick={handleSettings}
                  className={styles.settingsButton}
                  data-testid={'btn-cookie-pop-over-settings'}
                />
              </div>
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </>
  );
}

export default CookiePopOver;
